import { useCallback, useEffect } from "react";
import { useParams } from "react-router";

import { FilterButton, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { ClusterDetail } from "domains/clusters/components/ClusterDetail";
import { ClusterList } from "domains/clusters/components/ClusterList";
import { createClusterSelector, createClustersSelector } from "domains/clusters/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { fetchEmergingThemes } from "domains/monitor/emergingThemes/actions";
import {
  getEmergingThemesHasNextPage,
  getEmergingThemesIsPending,
  getEmergingThemesItems,
  getEmergingThemesOffset,
} from "domains/monitor/emergingThemes/selectors";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { getCanAccessMonitor } from "entities/permissions/selectors";
import { createTopicsSelector, getFollowedTopicsIds } from "entities/topics/selectors";

export const ClusterPage = () => {
  const dispatch = useDispatch();
  const items = useMemoisedSelector(getEmergingThemesItems, createClustersSelector);
  const followingTopics = useMemoisedSelector(getFollowedTopicsIds, createTopicsSelector);
  const { clusterId } = useParams();
  const canAccessMonitor = useSelector(getCanAccessMonitor);
  const hasNextPage = useSelector(getEmergingThemesHasNextPage);
  const isPending = useSelector(getEmergingThemesIsPending);
  const offset = useSelector(getEmergingThemesOffset);
  const siteLanguage = useSelector(getSiteLanguage);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const track = useTrack();
  const translate = useTranslate();

  const id = getPageId(clusterId, items);

  const cluster = useSelector(createClusterSelector(id));

  const selectedTopicsDependency = selectedTopics.join();

  useEffect(() => {
    if (items.length === 0) {
      dispatch(
        fetchEmergingThemes({
          offset: 0,
          filters: { topic: selectedTopics },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, items.length, selectedTopicsDependency]);

  useEffect(() => {
    if (items.length !== 0) {
      dispatch(
        fetchEmergingThemes({
          offset: 0,
          filters: { topic: selectedTopics },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, canAccessMonitor, siteLanguage, selectedTopicsDependency]);

  const clusterTitle = cluster?.title;
  useEffect(() => {
    if (!clusterId || !clusterTitle) {
      return;
    }
    track({
      entityId: clusterId,
      entityName: clusterTitle,
      entityType: "Cluster",
      name: "Browse page",
      page: "Monitor",
      subpage: "Cluster detail",
    });
  }, [clusterId, clusterTitle, track]);

  useEffect(() => {
    if (clusterId) {
      return;
    }
    track({ name: "Browse page", page: "Monitor", subpage: "Emerging Themes" });
  }, [clusterId, track]);

  const loadNextPage = useCallback(() => {
    dispatch(
      fetchEmergingThemes({
        offset: offset + 1,
        filters: { topic: selectedTopics },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, offset, selectedTopics.length]);

  const list = (
    <ClusterList
      hasNextPage={hasNextPage && canAccessMonitor}
      isCompact
      isOnCard
      isPending={isPending}
      items={items}
      loadNextPage={loadNextPage}
      selectedId={id}
      trackPage="Cluster detail"
    />
  );

  const openFilterModal = () =>
    dispatch(
      activateModal({
        id: "MonitorFilter",
      }),
    );

  const filterButton =
    followingTopics.length !== 0 && canAccessMonitor ? (
      <FilterButton count={selectedTopics.length} onClick={openFilterModal} />
    ) : undefined;

  return (
    <MasterDetailLayout
      detailBackNavigation={{ label: translate("app.monitor.emerging_themes"), path: "/monitor/emerging-themes" }}
      listBackNavigation={{ label: translate("app.monitor.title"), path: "/monitor" }}
      listComponent={list}
      listHeaderRightElement={filterButton}
      listTitle={translate("app.monitor.emerging_themes")}
      mainView={clusterId ? "right" : "left"}
    >
      {id && <ClusterDetail cluster={cluster} id={id} topicBack={{ term: "app.monitor.emerging_themes" }} />}
    </MasterDetailLayout>
  );
};
